import { Button } from "@/components/ui/button";
import { useAdminPerms } from "@/hooks/auth";
import { PlayerModalRefType, useClosePlayerModal } from "@/hooks/playerModal";
import { Loader2Icon } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useRef, useState } from "react";
import { useBackendApi } from "@/hooks/fetch";
import { GenericApiOkResp } from "@shared/genericApiTypes";
import type { AdminJailTemplatesDataType } from "@shared/otherTypes";
import type { PlayerModalPlayerData } from "@shared/playerApiTypes";
import { findActivePlayerAction } from "@shared/utils";
import { tsToLocaleDateTimeString } from "@/lib/dateTime";
import BanForm, { BanFormType } from "@/components/BanForm";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { txToast } from "@/components/TxToaster";
import { ModalTabInner, ModalTabMessage } from "@/components/modal-tabs";


type PlayerJailTabProps = {
    jailTemplates: AdminJailTemplatesDataType[];
    playerRef: PlayerModalRefType;
    player: PlayerModalPlayerData;
    serverTime: number;
};

export default function PlayerJailTab({ playerRef, jailTemplates, player, serverTime }: PlayerJailTabProps) {
    const jailFormRef = useRef<BanFormType>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isAnticheat, setIsAnticheat] = useState(false);
    const { hasPerm } = useAdminPerms();
    const canAnticheat = hasPerm('players.anticheat');
    const closeModal = useClosePlayerModal();
    const playerJailApi = useBackendApi<GenericApiOkResp>({
        method: 'POST',
        path: `/player/jail`,
        throwGenericErrors: true,
    });

    if (!hasPerm('players.jail')) {
        return <ModalTabMessage>
            You don't have permission to admin jail players.
        </ModalTabMessage>;
    }

    //Only one admin jail can be active at a time, the form stays visible but locked
    const activeJail = findActivePlayerAction(player.actionHistory, 'jail', serverTime);
    const isLocked = !!activeJail;

    const handleSave = () => {
        if (!jailFormRef.current) return;
        const { reason, duration } = jailFormRef.current.getData();

        if (!reason || reason.length < 3) {
            txToast.warning(`The reason must be at least 3 characters long.`);
            jailFormRef.current.focusReason();
            return;
        }

        setIsSaving(true);
        playerJailApi({
            queryParams: playerRef,
            data: { reason, duration, anticheat: isAnticheat },
            toastLoadingMessage: 'Jailing player...',
            genericHandler: {
                successMsg: 'Player admin jailed.',
            },
            finally: () => setIsSaving(false),
            success: () => closeModal(),
        });
    };

    const lockedHint = activeJail ? (
        <>
            This player is already admin jailed.<br />
            {activeJail.exp
                ? `Until ${tsToLocaleDateTimeString(activeJail.exp, 'medium', 'short')}`
                : 'Permanent'} ({activeJail.id})<br />
            Revoke it in the History tab before adding a new one.
        </>
    ) : null;

    //no permission, or there is already a jail to flag instead of a new one
    const anticheatBlocked = !canAnticheat || isLocked;

    return (
        <ModalTabInner className="grid gap-4">
            <BanForm
                ref={jailFormRef}
                banTemplates={jailTemplates}
                disabled={isSaving || isLocked}
                onNavigateAway={() => { closeModal(); }}
                templatesPath="/settings/admin-jail-templates"
                templatesLabel="Add Admin Jail Template"
                allowMinutes
                onTemplateSelect={(template) => setIsAnticheat(canAnticheat && !!template.anticheat)}
            />
            <div className="flex items-center justify-between gap-4">
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span
                            tabIndex={canAnticheat ? -1 : 0}
                            className={cn('inline-flex', anticheatBlocked && 'cursor-not-allowed')}
                        >
                            <label className={cn(
                                'flex items-center gap-2 text-sm select-none',
                                anticheatBlocked ? 'opacity-40 pointer-events-none' : 'cursor-pointer',
                            )}>
                                <Checkbox
                                    tone="jail"
                                    checked={isAnticheat}
                                    onCheckedChange={(checked) => setIsAnticheat(checked === true)}
                                    disabled={anticheatBlocked || isSaving}
                                />
                                Anticheat Jail
                            </label>
                        </span>
                    </TooltipTrigger>
                    {/* the active jail hint belongs to the submit button alone */}
                    {!canAnticheat ? (
                        <TooltipContent className="text-anticheat-jail text-center">
                            <p>
                                You need the "Anticheat Sanctions" permission<br />
                                to flag an admin jail as an anticheat jail.
                            </p>
                        </TooltipContent>
                    ) : null}
                </Tooltip>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span tabIndex={isLocked ? 0 : -1} className={isLocked ? 'cursor-not-allowed' : 'cursor-pointer'}>
                            <Button
                                size="sm"
                                variant="jail"
                                disabled={isSaving || isLocked}
                                onClick={handleSave}
                                className={isLocked ? 'pointer-events-none' : undefined}
                            >
                                {isSaving ? (
                                    <span className="flex items-center leading-relaxed">
                                        <Loader2Icon className="inline animate-spin h-4" /> Jailing...
                                    </span>
                                ) : 'Apply Admin Jail'}
                            </Button>
                        </span>
                    </TooltipTrigger>
                    {lockedHint ? (
                        <TooltipContent className="text-jail-inline text-center">
                            <p>{lockedHint}</p>
                        </TooltipContent>
                    ) : null}
                </Tooltip>
            </div>
        </ModalTabInner>
    );
}
